import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Send, MessageSquare, ChevronLeft, RefreshCw, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import AdminLayout from "./AdminLayout";

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function fmtTimeFull(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
}

function applyTemplate(body, ctx) {
  return body
    .replace(/{first_name}/g, (ctx.customer_name || "").split(" ")[0] || "there")
    .replace(/{order_number}/g, ctx.latest_order_number || "your order");
}

export default function AdminMessages() {
  const [threads, setThreads] = useState([]);
  const [activePhone, setActivePhone] = useState(null);
  const [thread, setThread] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [composePhone, setComposePhone] = useState("");
  const scrollRef = useRef(null);

  const refreshThreads = async () => {
    try {
      const r = await api.get("/admin/messages/threads");
      setThreads(r.data.threads || []);
    } catch {
      toast.error("Failed to load threads");
    } finally {
      setLoadingThreads(false);
    }
  };

  const loadThread = async (phone) => {
    setLoadingThread(true);
    try {
      const r = await api.get(`/admin/messages/threads/${encodeURIComponent(phone)}`);
      setThread(r.data);
      // Mark read in background — don't block UI.
      api.post(`/admin/messages/threads/${encodeURIComponent(phone)}/mark-read`)
        .then(() => refreshThreads())
        .catch(() => {});
    } catch {
      toast.error("Failed to load thread");
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => {
    refreshThreads();
    api.get("/admin/messages/templates").then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    const id = setInterval(refreshThreads, 15000); // poll every 15s
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (activePhone) loadThread(activePhone);
    else setThread(null);
  }, [activePhone]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [thread]);

  const ctx = thread?.context || {};

  const send = async () => {
    const to = activePhone || composePhone;
    if (!to || !draft.trim()) return;
    setSending(true);
    try {
      const r = await api.post("/admin/messages/send", { to, body: draft.trim() });
      const status = r.data.status;
      if (status === "sent") toast.success("Message sent");
      else if (status === "queued") toast.info("Saved — will send once Twilio toll-free is verified");
      else toast.warning(`Send failed: ${r.data.twilio_error || "unknown"}`);
      setDraft("");
      setShowCompose(false);
      setActivePhone(r.data.thread_phone);
      // Force re-load.
      await loadThread(r.data.thread_phone);
      refreshThreads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const totalUnread = useMemo(() => threads.reduce((a, t) => a + (t.unread_count || 0), 0), [threads]);

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-text)]">Messages</h1>
          <p className="text-xs muted-text mt-0.5">
            {totalUnread > 0 ? `${totalUnread} unread` : "All caught up"} · 2-way SMS via ScreenzZ number
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="messages-refresh-btn"
            onClick={() => { refreshThreads(); if (activePhone) loadThread(activePhone); }}
            className="p-2 rounded-full border-2 border-[var(--brand-border)] hover:brand-text"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            data-testid="messages-new-btn"
            onClick={() => { setShowCompose(true); setActivePhone(null); setDraft(""); }}
            className="px-3 py-2 rounded-full text-xs font-semibold brand-bg text-white"
          >
            New message
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[var(--brand-border)] overflow-hidden h-[calc(100vh-220px)] min-h-[480px] flex">
        {/* LEFT: thread list */}
        <aside className={`${activePhone || showCompose ? "hidden md:block" : "block"} w-full md:w-80 border-r border-[var(--brand-border)] overflow-y-auto`}>
          {loadingThreads ? (
            <p className="p-6 muted-text text-sm">Loading…</p>
          ) : threads.length === 0 ? (
            <div className="p-8 text-center">
              <MessageSquare className="w-8 h-8 mx-auto muted-text mb-2" />
              <p className="muted-text text-sm">No messages yet.</p>
              <p className="text-xs muted-text mt-1">When customers SMS your ScreenzZ number, threads appear here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--brand-border)]">
              {threads.map((t) => (
                <li key={t.phone}>
                  <button
                    data-testid={`thread-${t.phone}`}
                    onClick={() => { setShowCompose(false); setActivePhone(t.phone); }}
                    className={`w-full text-left p-4 hover:bg-[#f7faf6] ${activePhone === t.phone ? "bg-[var(--brand-green-light)]" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-[var(--brand-text)] truncate">
                          {t.customer_name || t.phone}
                        </p>
                        <p className="text-[11px] muted-text truncate">{t.phone}{t.latest_order_number ? ` · ${t.latest_order_number}` : ""}</p>
                        <p className={`text-xs mt-1 truncate ${t.unread_count > 0 ? "font-semibold text-[var(--brand-text)]" : "muted-text"}`}>
                          {t.last_message?.direction === "out" ? "You: " : ""}
                          {t.last_message?.body || ""}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="text-[10px] muted-text">{fmtTime(t.last_message?.created_at)}</span>
                        {t.unread_count > 0 && (
                          <span className="brand-bg text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5 inline-flex items-center justify-center">
                            {t.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* RIGHT: conversation */}
        <section className={`${activePhone || showCompose ? "flex" : "hidden md:flex"} flex-1 flex-col min-w-0`}>
          {!activePhone && !showCompose ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center max-w-xs px-6">
                <MessageSquare className="w-10 h-10 mx-auto muted-text mb-2" />
                <p className="muted-text text-sm">Pick a conversation on the left, or click <strong>New message</strong> to start one.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="border-b border-[var(--brand-border)] p-4 flex items-center gap-3">
                <button
                  onClick={() => { setActivePhone(null); setShowCompose(false); }}
                  className="md:hidden p-1 -ml-1"
                  aria-label="Back"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex-1 min-w-0">
                  {showCompose ? (
                    <div>
                      <p className="text-xs font-bold uppercase muted-text mb-1">To</p>
                      <input
                        data-testid="compose-phone-input"
                        type="tel"
                        autoFocus
                        placeholder="(555) 123-4567"
                        value={composePhone}
                        onChange={(e) => setComposePhone(e.target.value)}
                        className="rounded-input"
                      />
                    </div>
                  ) : (
                    <>
                      <p className="font-bold text-[var(--brand-text)] truncate">
                        {ctx.customer_name || activePhone}
                      </p>
                      <p className="text-[11px] muted-text flex items-center gap-2 flex-wrap">
                        <Phone className="w-3 h-3" /> {activePhone}
                        {ctx.latest_order_number && (
                          <Link
                            to="/admin/orders"
                            className="brand-text underline ml-1"
                            data-testid="thread-order-link"
                          >
                            {ctx.latest_order_number}
                          </Link>
                        )}
                        {ctx.latest_order_status && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[var(--brand-green-light)] brand-text">
                            {ctx.latest_order_status.replaceAll("_", " ")}
                          </span>
                        )}
                      </p>
                    </>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#f7faf6]">
                {loadingThread ? (
                  <p className="muted-text text-sm">Loading…</p>
                ) : showCompose ? (
                  <p className="muted-text text-sm text-center mt-8">New conversation.</p>
                ) : (thread?.messages || []).length === 0 ? (
                  <p className="muted-text text-sm text-center mt-8">No messages yet.</p>
                ) : (
                  (thread?.messages || []).map((m) => {
                    const out = m.direction === "out";
                    return (
                      <div key={m.id} className={`flex ${out ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${out ? "brand-bg text-white" : "bg-white border border-[var(--brand-border)] text-[var(--brand-text)]"}`}>
                          <p className="whitespace-pre-wrap break-words">{m.body}</p>
                          <p className={`text-[10px] mt-1 ${out ? "text-white/70" : "muted-text"}`}>
                            {fmtTimeFull(m.created_at)}
                            {out && m.status === "queued" && " · queued"}
                            {out && m.status === "failed" && " · failed"}
                            {out && m.status === "sent" && " · sent"}
                          </p>
                          {out && m.status === "failed" && m.twilio_error && (
                            <p className="text-[10px] mt-0.5 text-red-100">{m.twilio_error}</p>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Templates */}
              {templates.length > 0 && (
                <div className="px-4 pt-2 flex gap-2 overflow-x-auto border-t border-[var(--brand-border)]">
                  <Sparkles className="w-3.5 h-3.5 mt-2 muted-text shrink-0" />
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      data-testid={`template-${tpl.id}`}
                      onClick={() => setDraft(applyTemplate(tpl.body, ctx))}
                      className="text-[11px] font-semibold px-3 py-1.5 rounded-full border border-[var(--brand-border)] hover:border-[var(--brand-green)] hover:brand-text whitespace-nowrap shrink-0"
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
              )}

              {/* Composer */}
              <form
                onSubmit={(e) => { e.preventDefault(); send(); }}
                className="p-3 border-t border-[var(--brand-border)] flex items-end gap-2"
              >
                <textarea
                  data-testid="message-composer"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Type a message…"
                  rows={2}
                  className="flex-1 rounded-2xl border-2 border-[var(--brand-border)] bg-white px-3 py-2 text-sm focus:border-[var(--brand-green)] outline-none resize-none"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                />
                <button
                  data-testid="message-send-btn"
                  type="submit"
                  disabled={sending || !draft.trim() || (showCompose && !composePhone.trim())}
                  className="brand-bg text-white px-4 py-2 rounded-full font-semibold flex items-center gap-1.5 disabled:opacity-40"
                >
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">{sending ? "Sending…" : "Send"}</span>
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
